// src/pages/deliveryNote/CreateDeliveryNotePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Truck, X, Search, AlertCircle, Package, ShoppingBag, User, Receipt } from 'lucide-react';
import { createDeliveryNote } from '../../services/api/offlineSales/deliveryNotes';
import { getOrder, getOrders } from '../../services/api/offlineSales/orders';
import { getUserWarehouses } from '../../services/api/returnBatch';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { getImageUrl } from '../../utils/imageUtils';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { InfoCard } from '../../components/common';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './CreateDeliveryNotePage.module.css';

const CreateDeliveryNotePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [orderIdInput, setOrderIdInput] = useState(searchParams.get('orderNumber') || '');
  const [order, setOrder] = useState(null);
  const backTarget = order
    ? `/offline-sales/orders/${order.id}`
    : '/offline-sales/delivery-notes';
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [orderOptions, setOrderOptions] = useState([]);
  const [isSearchingOrder, setIsSearchingOrder] = useState(false);

  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [receivedByName, setReceivedByName] = useState('');
  const [notes, setNotes] = useState('');
  const [itemQuantities, setItemQuantities] = useState({}); // orderItemId -> quantity
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const result = await getUserWarehouses();
        const warehouseList = result.warehouses || [];
        setWarehouses(warehouseList);
        if (warehouseList.length === 1) setWarehouseId(warehouseList[0].id);
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (searchParams.get('orderNumber')) {
      handleLookupOrder(searchParams.get('orderNumber'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remainingQty = (item) => Math.max(0, parseInt(item.quantity, 10) - parseFloat(item.deliveredQty || 0));

  const toOrderOption = (o) => ({
    value: o.id,
    label: `${o.orderNumber}${o.customer?.name ? ` — ${o.customer.name}` : ''}`,
  });

  // Searchable lookup, same as GRN/RTV create pages: orders matching the typed order number; an
  // all-digit term is also resolved as an order id. Selecting an option runs handleLookupOrder.
  const searchOrders = async (term = '') => {
    const value = term.trim();
    try {
      setIsSearchingOrder(true);
      const { orders } = await getOrders({ search: value || undefined, limit: 20 });
      const results = (orders || []).filter(o => o.status !== 'cancelled');
      if (/^d+$/.test(value) && !results.some(o => String(o.id) === value)) {
        try {
          const byId = await getOrder(value);
          if (byId && byId.status !== 'cancelled') results.unshift(byId);
        } catch (err) {
          // not a valid id — number search results only
        }
      }
      setOrderOptions(results.map(toOrderOption));
    } catch (err) {
      console.error('Failed to search orders:', err);
      setOrderOptions([]);
    } finally {
      setIsSearchingOrder(false);
    }
  };

  useEffect(() => {
    searchOrders('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLookupOrder = async (idOverride) => {
    const orderIdValue = (idOverride ?? orderIdInput).trim();
    if (!orderIdValue) return;
    try {
      setIsLookingUp(true);
      setLookupError('');
      // A numeric value is the real numeric primary key — look it up directly. Anything else
      // (a user-typed order number, or the ?orderNumber= pre-fill from the order list/detail
      // pages, e.g. "VSS2609110002") is not a valid id, so search by order number instead of
      // feeding a non-numeric value into the numeric-id endpoint.
      let result;
      if (/^\d+$/.test(orderIdValue)) {
        result = await getOrder(orderIdValue);
      } else {
        const { orders } = await getOrders({ search: orderIdValue });
        const match = (orders || []).find(o => o.orderNumber?.toLowerCase() === orderIdValue.toLowerCase());
        if (!match) throw new Error(t('deliveryNote.orderNotFound'));
        // The search/list endpoint returns a lightweight summary (no nested Product/
        // ProductVariation per item) — fetch the full order by id, same as the numeric path,
        // so the items table gets complete item data instead of "undefined" names.
        result = await getOrder(match.id);
      }
      setOrder(result);
      setOrderOptions(prev => (prev.some(option => option.value === result.id) ? prev : [toOrderOption(result), ...prev]));
      setOrderIdInput(result.orderNumber || String(result.id));
      const initialQuantities = {};
      (result.orderItems || []).forEach(item => {
        initialQuantities[item.id] = remainingQty(item);
      });
      setItemQuantities(initialQuantities);
    } catch (err) {
      setOrder(null);
      setLookupError(err.response?.data?.message || t('deliveryNote.orderNotFound'));
    } finally {
      setIsLookingUp(false);
    }
  };

  const updateQuantity = (orderItemId, value, max) => {
    const clamped = Math.min(Math.max(0, parseInt(value, 10) || 0), max);
    setItemQuantities(prev => ({ ...prev, [orderItemId]: clamped }));
  };

  const hasItemsSelected = Object.values(itemQuantities).some(q => q > 0);

  const validateForm = () => {
    const newErrors = {};
    if (!order) newErrors.order = t('deliveryNote.orderRequired');
    if (!hasItemsSelected) newErrors.items = t('deliveryNote.atLeastOneItemRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('deliveryNote.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const items = Object.entries(itemQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([orderItemId, qty]) => ({
          orderItemId: parseInt(orderItemId),
          quantityDelivered: qty,
        }));

      const payload = {
        orderId: order.id,
        warehouseId: warehouseId ? parseInt(warehouseId) : null,
        receivedByName: receivedByName.trim() || '',
        notes: notes.trim() || '',
        items,
      };

      const response = await createDeliveryNote(payload);
      success(t('deliveryNote.deliveryNoteCreatedSuccess', { number: response.deliveryNote?.deliveryNoteNumber }), {
        title: t('common.success'),
      });
      navigate(`/offline-sales/delivery-notes/${response.deliveryNote.id}`);
    } catch (err) {
      console.error('Delivery note creation error:', err);
      error(err.response?.data?.message || t('deliveryNote.failedToCreateDeliveryNote'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSelected = Object.values(itemQuantities).reduce((sum, q) => sum + q, 0);

  return (
    <div className={styles.createDeliveryNotePage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Truck className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('deliveryNote.createDeliveryNote')}</h1>
              <p>{t('deliveryNote.createDeliveryNoteDescription')}</p>
            </div>
          </div>
          <button onClick={() => navigate(backTarget)} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('deliveryNote.backToDeliveryNotes')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.deliveryNoteForm}>
        <InfoCard icon={Search} title={t('deliveryNote.lookupOrder')}>
            <div className={styles.lookupRowWrapper}>
              <div className={styles.lookupRow}>
                <Dropdown
                  className={styles.lookupDropdown}
                  value={order?.id || ''}
                  onChange={value => { if (value) handleLookupOrder(String(value)); }}
                  options={orderOptions}
                  placeholder={isLookingUp ? t('common.loading') : t('deliveryNote.orderIdPlaceholder')}
                  searchPlaceholder={t('deliveryNote.searchOrder')}
                  onSearch={searchOrders}
                  loading={isSearchingOrder}
                  loadingText={t('common.loading')}
                  noOptionsText={t('deliveryNote.noOrdersFound')}
                  allowClear={false}
                  disabled={isLookingUp}
                />
              </div>
              {order && (
                <div className={styles.orderSummary}>
                  <span className={styles.summaryItem}><ShoppingBag size={14} /><strong>{order.orderNumber}</strong></span>
                  <span className={styles.summaryItem}><User size={14} />{order.customer?.name || t('common.na')}</span>
                  <span className={styles.summaryItem}><Receipt size={14} />{formatCurrency(order.grandTotal)}</span>
                </div>
              )}
            </div>
            {lookupError && <div className={styles.sectionError}><AlertCircle size={16} />{lookupError}</div>}
            {errors.order && <div className={styles.sectionError}><AlertCircle size={16} />{errors.order}</div>}
        </InfoCard>

        {order && (
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              <InfoCard icon={Truck} title={t('deliveryNote.details')}>
                  <div className={styles.fieldsGrid}>
                    <div className={styles.fieldGroup}>
                      <label>{t('deliveryNote.warehouse')}</label>
                      <Dropdown
                        value={warehouseId}
                        onChange={value => setWarehouseId(value || '')}
                        options={warehouses.map(w => ({ value: w.id, label: w.name }))}
                        placeholder={t('deliveryNote.selectWarehouse')}
                        searchable
                      />
                    </div>
                    <div className={styles.fieldGroup}>
                      <label>{t('deliveryNote.receivedByName')}</label>
                      <input
                        type="text"
                        value={receivedByName}
                        onChange={(e) => setReceivedByName(e.target.value)}
                        className={styles.input}
                        placeholder={t('deliveryNote.receivedByNamePlaceholder')}
                      />
                    </div>
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>{t('deliveryNote.notes')}</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className={styles.textarea}
                      placeholder={t('deliveryNote.notesPlaceholder')}
                    />
                  </div>
              </InfoCard>
            </div>

            <div className={styles.rightColumn}>
              <InfoCard icon={Package} title={t('deliveryNote.itemsToDeliver')}>
                  <div className={ps.tableWrapper}>
                  <table className={ps.itemsTable}>
                    <thead>
                      <tr>
                        <th>{t('deliveryNote.item')}</th>
                        <th className={ps.alignRight}>{t('deliveryNote.orderedQty')}</th>
                        <th className={ps.alignRight}>{t('deliveryNote.remainingQty')}</th>
                        <th className={ps.alignRight}>{t('deliveryNote.quantityToDeliver')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.orderItems || []).map(item => {
                        const remaining = remainingQty(item);
                        return (
                        <tr key={item.id} className={remaining <= 0 ? styles.disabledRow : ''}>
                          <td>
                            <div className={ps.productCellInner}>
                              <ProductThumb src={getImageUrl(item.Product?.image)} alt={item.Product?.name || ''} />
                              <div className={ps.productCell}>
                                <span className={ps.productName}>{item.Product?.name}</span>
                                {item.ProductVariation?.sku && <span className={ps.productSku}>SKU: {item.ProductVariation.sku}</span>}
                              </div>
                            </div>
                          </td>
                          <td className={`${ps.alignRight} ${ps.amountCell}`}>{item.quantity}</td>
                          <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{remaining}</td>
                          <td className={ps.alignRight}>
                            <input
                              type="number"
                              min="0"
                              max={remaining}
                              step="1"
                              value={itemQuantities[item.id] ?? 0}
                              onChange={(e) => updateQuantity(item.id, e.target.value, remaining)}
                              disabled={remaining <= 0}
                              className={styles.qtyInput}
                            />
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  {errors.items && <div className={styles.sectionError}><AlertCircle size={16} />{errors.items}</div>}
              </InfoCard>
            </div>
          </div>
        )}

        <div className={styles.actionBarSpacer} />
      </form>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            {totalSelected > 0 && (
              <span className={styles.actionBarItemCount}>
                {totalSelected} {t('deliveryNote.unitsSelected')}
              </span>
            )}
          </div>
          <div className={styles.actionBarButtons}>
            <button type="button" onClick={() => navigate(backTarget)} className={styles.cancelButton} disabled={isSubmitting}>
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting || !order}>
              {isSubmitting ? <div className={styles.loadingSpinner} /> : (
                <>
                  <Save size={15} />
                  {t('deliveryNote.createDeliveryNote')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDeliveryNotePage;
